/**
 * Purchase orders — list and detail with receiving.
 *
 * Functional spec: `qt_poc/po_qt.py`.
 *
 * **`status` and `received_pct` are not editable.** The server derives both
 * from the line quantities (`PurchaseOrder.recalculate_receipt`), so a payload
 * asserting `status: "RECEIVED"` is discarded. The UI therefore shows them as
 * read-only facts and drives them by editing `qty_received`, which is how the
 * desktop works too.
 */
import { useMemo, useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Card, Group, NumberInput, Paper, Progress,
  Select, SimpleGrid, Stack, Text, TextInput, Title,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useNavigate, useParams } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type { POLine, PurchaseOrder } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { DataGrid } from '../components/DataGrid'
import { FilterPanel } from '../components/FilterPanel'
import { StatusBadge } from '../components/StatusBadge'
import { EM_DASH, fmtDate, fmtMoney, fmtQty } from '../format'
import { CODE_STYLE, FILL_PANEL } from '../theme'
import { confirmAction, confirmDelete } from '../components/confirm'
import { ManualPOModal, POHeaderModal, POLineModal } from './OrderDialogs'

export function OrdersPage() {
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const pos = useQuery({ queryKey: ['pos'], queryFn: () => api.get<PurchaseOrder[]>('/pos') })

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (pos.data ?? []).filter(
      (p) =>
        (!term ||
          p.po_number.toLowerCase().includes(term) ||
          p.project_name.toLowerCase().includes(term)) &&
        (!status || p.status === status),
    )
  }, [pos.data, search, status])

  const columns = useMemo<ColumnDef<PurchaseOrder, any>[]>(
    () => [
      { accessorKey: 'po_number', header: 'מספר הזמנה', meta: { code: true } },
      { accessorKey: 'project_name', header: 'פרויקט' },
      {
        accessorKey: 'order_date',
        header: 'תאריך הזמנה',
        meta: { hideBelow: 'lg' },
        cell: (c) => fmtDate(c.getValue<string>()),
      },
      {
        accessorKey: 'final_price_usd',
        header: 'סה"כ USD',
        meta: { numeric: true },
        cell: (c) => fmtMoney(c.getValue<number>()),
      },
      {
        accessorKey: 'status',
        header: 'סטטוס',
        meta: { nowrap: true },
        cell: (c) => <StatusBadge value={c.getValue<string>()} />,
      },
      {
        accessorKey: 'received_pct',
        header: '% נתקבל',
        meta: { numeric: true, hideBelow: 'md' },
        cell: (c) => `${fmtQty(c.getValue<number>())}%`,
      },
    ],
    [],
  )

  return (
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      <Group justify="space-between">
        <Title order={2}>הזמנות רכש</Title>
        {canEdit && (
          <Button onClick={() => setCreating(true)}>הזמנה ידנית</Button>
        )}
      </Group>
      <FilterPanel
        storageKey="orders"
        search={
          <TextInput
            label="חיפוש"
            placeholder="מספר הזמנה או פרויקט…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ minWidth: 260 }}
          />
        }
      >
        <Select
          label="סטטוס"
          clearable
          clearButtonProps={{ "aria-label": "נקה", size: "md" }}
          placeholder="הכל"
          data={['OPEN', 'SUBMITTED', 'PARTIAL', 'RECEIVED', 'CLOSED', 'CANCELLED']}
          value={status}
          onChange={setStatus}
          style={{ minWidth: 170 }}
        />
      </FilterPanel>
      <Paper withBorder style={FILL_PANEL}>
        <DataGrid
          data={rows}
          columns={columns}
          loading={pos.isLoading}
          empty="אין הזמנות להצגה"
          onRowClick={(po) => navigate(`/orders/${po.id}`)}
          maxHeight="100%"
        />
      </Paper>

      <ManualPOModal
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(po) => { setCreating(false); navigate(`/orders/${po.id}`) }}
      />
    </Stack>
  )
}

export function OrderDetailPage() {
  const { id } = useParams()
  const poId = Number(id)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { canEdit, canDelete } = useAuth()
  const [draft, setDraft] = useState<PurchaseOrder | null>(null)
  const [editingHeader, setEditingHeader] = useState(false)
  // `undefined` = closed, `null` = adding a line, a number = editing that row.
  //
  // The *index*, not the line object: the rows come from `draft ?? po.data`, so
  // the object identity a handler captured is not the object the next render
  // holds. Comparing them silently matched nothing and the delete did nothing.
  const [editingLine, setEditingLine] = useState<number | null | undefined>(undefined)

  const po = useQuery({
    queryKey: ['po', poId],
    queryFn: () => api.get<PurchaseOrder>(`/pos/${poId}`),
    enabled: Number.isFinite(poId),
  })

  const current = draft ?? po.data ?? null
  const dirty = draft != null

  const save = useMutation({
    mutationFn: (value: PurchaseOrder) => api.put<PurchaseOrder>(`/pos/${poId}`, value),
    onSuccess: (saved) => {
      // Replace the draft with what came back, not with what was sent: the
      // server recomputes status and received_pct, so the response is the only
      // accurate picture.
      setDraft(null)
      qc.setQueryData(['po', poId], saved)
      void qc.invalidateQueries({ queryKey: ['pos'] })
      notifications.show({ message: 'ההזמנה נשמרה', color: 'green' })
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'שמירת ההזמנה נכשלה',
      }),
  })

  const remove = useMutation({
    mutationFn: () => api.delete<unknown>(`/pos/${poId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pos'] })
      notifications.show({ message: 'ההזמנה נמחקה', color: 'green' })
      navigate('/orders')
    },
    // Refused unless the order is CANCELLED or CLOSED — the server says so, and
    // changing the status first is the actual next step.
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'מחיקת ההזמנה נכשלה',
      }),
  })

  /**
   * Lines are saved with the whole order, because `PODAO.save` replaces the
   * line set and recomputes the totals from it. There is no per-line endpoint
   * and there should not be: a line and the order's total are one fact.
   */
  function saveLine(line: POLine) {
    const base = draft ?? po.data!
    const index = editingLine
    const lines = index == null
      ? [...base.lines, line]
      : base.lines.map((l, i) => (i === index ? line : l))
    setEditingLine(undefined)
    save.mutate({ ...base, lines })
  }

  function deleteLine(index: number) {
    const base = draft ?? po.data!
    save.mutate({ ...base, lines: base.lines.filter((_, i) => i !== index) })
  }

  const columns = useMemo<ColumnDef<PurchaseOrder['lines'][number], any>[]>(
    () => [
      { accessorKey: 'sku', header: 'מקט יצרן', meta: { code: true } },
      { accessorKey: 'description', header: 'תיאור' },
      { accessorKey: 'qty_to_order', header: 'כמות', meta: { numeric: true }, cell: (c) => fmtQty(c.getValue<number>()) },
      {
        id: 'received',
        header: 'התקבל',
        meta: { numeric: true },
        accessorFn: (r) => r.qty_received,
        cell: (c) => {
          const line = c.row.original
          if (!canEdit) return fmtQty(line.qty_received)
          return (
            <NumberInput
              size="xs"
              hideControls
              w={100}
              min={0}
              max={line.qty_to_order}
              decimalScale={2}
              value={line.qty_received}
              onChange={(v) => {
                const qty = Number(v) || 0
                setDraft((d) => {
                  const base = d ?? structuredClone(po.data!)
                  return {
                    ...base,
                    lines: base.lines.map((l) =>
                      l.sku === line.sku && l.description === line.description
                        ? { ...l, qty_received: qty }
                        : l,
                    ),
                  }
                })
              }}
            />
          )
        },
      },
      {
        accessorKey: 'received_date',
        header: 'תאריך קבלה',
        cell: (c) => fmtDate(c.getValue<string>()),
      },
      {
        accessorKey: 'unit_price_usd',
        header: 'מחיר יחידה',
        meta: { numeric: true },
        cell: (c) => fmtMoney(c.getValue<number>()),
      },
      {
        id: 'total',
        header: 'סה"כ USD',
        meta: { numeric: true },
        accessorFn: (r) => r.qty_to_order * r.unit_price_usd,
        cell: (c) => fmtMoney(c.getValue<number>()),
      },
      {
        id: 'act',
        header: '',
        meta: { nowrap: true, actions: true },
        cell: (c) =>
          canEdit ? (
            <Group gap={4} wrap="nowrap">
              <Button
                size="xs"
                variant="subtle"
                onClick={() => setEditingLine(c.row.index)}
              >
                ערוך
              </Button>
              <ActionIcon
                size={30} color="red" variant="subtle" aria-label="מחק שורה"
                onClick={() => {
                  void confirmDelete(`השורה "${c.row.original.sku}"`).then(
                    (ok) => ok && deleteLine(c.row.index))
                }}
              >
                ✕
              </ActionIcon>
            </Group>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, po.data, draft],
  )

  if (po.isLoading) return <Text>טוען…</Text>
  if (!current) return <Alert color="red">ההזמנה לא נמצאה.</Alert>

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" size="compact-sm" onClick={() => navigate('/orders')}>
            ← חזרה
          </Button>
          <Title order={2} style={CODE_STYLE}>{current.po_number}</Title>
          {canEdit ? (
            // The desktop's status menu (`_set_status`). OPEN/SUBMITTED/
            // PARTIAL/RECEIVED are *derived* from the received quantities and
            // the server recomputes them on save, so choosing one of those is
            // only ever a hint. CANCELLED and CLOSED are not derived: they
            // stick, and they are what makes an order deletable.
            <Select
              size="xs"
              w={150}
              aria-label="סטטוס ההזמנה"
              data={[
                { value: 'OPEN', label: 'פתוחה' },
                { value: 'SUBMITTED', label: 'הוגשה לספק' },
                { value: 'PARTIAL', label: 'התקבלה חלקית' },
                { value: 'RECEIVED', label: 'התקבלה' },
                { value: 'CLOSED', label: 'סגורה' },
                { value: 'CANCELLED', label: 'בוטלה' },
              ]}
              value={current.status}
              onChange={(v) => {
                if (!v || v === current.status) return
                const verb = v === 'CANCELLED' ? 'לבטל' : v === 'CLOSED' ? 'לסגור' : null
                if (!verb) {
                  save.mutate({ ...current, status: v })
                  return
                }
                void confirmAction({
                  title: v === 'CANCELLED' ? 'ביטול הזמנה' : 'סגירת הזמנה',
                  message: `${verb} את הזמנה ${current.po_number}?`,
                  // Not "בטל": that is the word on the *cancel* button, so a
                  // confirm button carrying it would ask the user to press
                  // "cancel" in order to cancel the order.
                  confirmLabel: v === 'CANCELLED' ? 'אשר ביטול' : 'סגור הזמנה',
                  danger: v === 'CANCELLED',
                }).then((ok) => ok && save.mutate({ ...current, status: v }))
              }}
            />
          ) : (
            <StatusBadge value={current.status} />
          )}
        </Group>
        <Group gap="xs">
          <Button
            variant="default"
            onClick={() => void api.download(`/pos/${poId}/export.csv`, `${current.po_number}.csv`)}
          >
            ייצוא לספק
          </Button>
          {canEdit && (
            <>
              <Button variant="light" onClick={() => setEditingHeader(true)}>
                ערוך הזמנה
              </Button>
              <Button variant="light" onClick={() => setEditingLine(null)}>
                הוסף שורה
              </Button>
            </>
          )}
          {canDelete && (
            <Button
              variant="subtle"
              color="red"
              loading={remove.isPending}
              onClick={() => {
                void confirmDelete(`הזמנה ${current.po_number} לצמיתות`).then(
                  (ok) => ok && remove.mutate())
              }}
            >
              מחק הזמנה
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => save.mutate(current)} loading={save.isPending} disabled={!dirty}>
              שמור קבלה
            </Button>
          )}
        </Group>
      </Group>

      {dirty && (
        <Alert color="yellow" variant="light">
          יש שינויים שלא נשמרו. הסטטוס ואחוז הקבלה מחושבים בשרת ויתעדכנו לאחר
          השמירה.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">פרויקט</Text>
          <Text fw={600}>{current.project_name || EM_DASH}</Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">סה"כ הזמנה</Text>
          <Text fw={700} size="lg">{fmtMoney(current.final_price_usd)}</Text>
          <Text size="xs" c="dimmed">
            ₪{current.total_ils.toLocaleString('he-IL', { minimumFractionDigits: 2 })} · שער {current.exchange_rate_used}
          </Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">מע"מ / משלוח</Text>
          <Text fw={600}>{current.vat_pct}% / {current.shipping_pct}%</Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">אחוז קבלה</Text>
          <Group gap="xs" align="baseline">
            <Text fw={700} size="lg">{fmtQty(po.data?.received_pct ?? 0)}%</Text>
            <Badge size="xs" variant="light">מחושב בשרת</Badge>
          </Group>
          <Progress mt={6} value={po.data?.received_pct ?? 0} color="teal" />
        </Card>
      </SimpleGrid>

      <Paper withBorder>
        <DataGrid data={current.lines} columns={columns} empty="אין שורות בהזמנה" maxHeight="50vh" />
      </Paper>

      <POHeaderModal
        po={editingHeader ? current : null}
        saving={save.isPending}
        onClose={() => setEditingHeader(false)}
        onSave={(next) => { setEditingHeader(false); save.mutate(next) }}
      />
      <POLineModal
        opened={editingLine !== undefined}
        line={editingLine == null ? null : current.lines[editingLine] ?? null}
        onClose={() => setEditingLine(undefined)}
        onSave={saveLine}
      />
    </Stack>
  )
}
